import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { useChannels } from '@/api/endpoints/channels'
import {
  useNotificationCatalog,
  useNotificationSettings,
  useUpdateNotificationSetting,
  type EventCatalogOut,
  type EventSettingOut,
} from '@/api/endpoints/notifications'
import { Section } from '@/components/ds/Section'
import { Toggle } from '@/components/ui/Toggle'
import { Button } from '@/components/ui/Button'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { TemplateEditorDialog } from '@/features/notifications/components/TemplateEditorDialog'
import styles from './NotificationEventsSettings.module.scss'
import { errorMessage } from '@/lib/errors'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

const GLOBAL = '__global__'

const CATEGORY_LABELS: Record<string, string> = {
  system: 'Sistema',
  pipeline: 'Pipeline',
  upload: 'Upload',
  scheduler: 'Scheduler',
  signup: 'Signup',
  digest: 'Digest',
}
const CATEGORY_ORDER = ['system', 'pipeline', 'upload', 'scheduler', 'signup', 'digest']

function isCustomized(s: EventSettingOut | undefined): boolean {
  if (!s) return false
  return s.title_template != null || s.body_template != null || s.mention != null
}

/**
 * Discord events: one toggle and one text template per event, global or
 * per channel. Lives in the "Notificações" tab of Configurações — it used
 * to be a page of its own (/notifications, which now redirects there).
 */
export function NotificationEventsSettings() {
  const [scope, setScope] = useState<string>(GLOBAL)
  const [editingKey, setEditingKey] = useState<string | null>(null)

  const channelSlug = scope === GLOBAL ? null : scope
  const catalogQuery = useNotificationCatalog()
  const settingsQuery = useNotificationSettings(channelSlug)
  const channelsQuery = useChannels()
  const update = useUpdateNotificationSetting()

  const settingsByKey = useMemo(() => {
    const m = new Map<string, EventSettingOut>()
    for (const s of settingsQuery.data?.events ?? []) m.set(s.event_key, s)
    return m
  }, [settingsQuery.data])

  const grouped = useMemo(() => {
    const g = new Map<string, EventCatalogOut[]>()
    for (const ev of catalogQuery.data?.events ?? []) {
      const arr = g.get(ev.category) ?? []
      arr.push(ev)
      g.set(ev.category, arr)
    }
    return g
  }, [catalogQuery.data])

  function toggleEnabled(ev: EventCatalogOut, next: boolean) {
    update.mutate(
      { event_key: ev.key, channel_slug: channelSlug, enabled: next },
      { onError: (err) => toast.error(errorMessage(err, 'Falha ao salvar')) },
    )
  }

  const editingEvent = catalogQuery.data?.events.find((e) => e.key === editingKey) ?? null
  const editingSetting = editingKey ? settingsByKey.get(editingKey) : undefined

  const isLoading = catalogQuery.isLoading || settingsQuery.isLoading
  const scopeOptions = [
    { value: GLOBAL, label: 'Global (padrão)' },
    ...(channelsQuery.data ?? []).map((c) => ({ value: c.slug, label: c.name })),
  ]

  return (
    <div className={styles.stack}>
      <Section
        title="Eventos do Discord"
        description="Ligue/desligue cada evento e personalize o texto com variáveis. Global vale para todos os canais; um canal específico sobrescreve o global só para ele."
      >
        <SelectRoot value={scope} onValueChange={setScope}>
          <SelectTrigger className={styles.scopeSelect} aria-label="Escopo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {scopeOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      </Section>

      {settingsQuery.isError ? (
        <p className={styles.warning}>
          Não foi possível carregar suas customizações — mostrando os textos padrão. Editar e salvar
          ainda funciona.
        </p>
      ) : null}

      {isLoading ? (
        <p className={styles.status}>Carregando…</p>
      ) : catalogQuery.isError ? (
        <QueryErrorNotice
          error={catalogQuery.error}
          label="o catálogo de eventos"
          onRetry={() => {
            void catalogQuery.refetch()
          }}
        />
      ) : (
        CATEGORY_ORDER.filter((cat) => grouped.has(cat)).map((cat) => (
          <Section key={cat} title={CATEGORY_LABELS[cat] ?? cat}>
            <ul className={styles.list}>
              {(grouped.get(cat) ?? []).map((ev) => {
                const setting = settingsByKey.get(ev.key)
                const enabled = setting?.effective_enabled ?? ev.default_enabled
                return (
                  <li key={ev.key} className={styles.row}>
                    <div className={styles.rowMain}>
                      <span className={styles.rowLabel}>
                        {ev.label}
                        {!ev.active ? <span className={styles.inactiveTag}>inativo</span> : null}
                      </span>
                      <span className={styles.rowMeta}>
                        {isCustomized(setting) ? 'Texto customizado' : 'Texto padrão'}
                      </span>
                    </div>
                    <div className={styles.rowActions}>
                      <Toggle
                        checked={enabled}
                        onCheckedChange={(v) => toggleEnabled(ev, v)}
                        aria-label={`Ativar ${ev.label}`}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingKey(ev.key)}
                      >
                        Editar texto
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </Section>
        ))
      )}

      {editingEvent ? (
        <TemplateEditorDialog
          event={editingEvent}
          setting={editingSetting}
          channelSlug={channelSlug}
          onClose={() => setEditingKey(null)}
        />
      ) : null}
    </div>
  )
}
